import microuniversoApi from "app/api/microuniversoApi";
import store from "app/redux/store/store";
import { atualizarCamposPendentesOrcamentoEmAndamentoAction } from "app/redux/orcamentoEmAndamentoSlice";
import { useAppSelector } from "app/redux/hooks";
import { Endereco } from "app/types/Endereco";
import { IColuna } from "app/types/IColuna";
import { IOrcamento } from "app/types/IOrcamento";
import { Obra } from "app/types/Obra";
import { Parametro } from "app/types/Parametro";
import { tratamentoErroPadrao } from "app/use-cases";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { alterarObra } from "app/use-cases/orcamento/alterarObra";
import { SalvarEnderecoOrcamento } from "app/use-cases/orcamento/endereco-orcamento/enderecoOrcamento";
import { obterParametros } from "app/use-cases/parametro/obterParametros";
import globalMU from "globalMU";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { substituirItemEmArray } from "substituirItemEmArray";
import { Breadcrumb2Container } from "../breadcrumb2/Breadcrumb2Container";
import { GridEnderecosOrcamento } from "./grid-enderecosOrcamento/GridEnderecosOrcamento";

export const GridEnderecosContainer = React.memo(() => {
    const nomeGrid: string = "ENDERECO_ORCAMENTO_TABELA";
    const orcamento: IOrcamento = useAppSelector((store) => store.orcamento);
    const [enderecos, setEnderecos] = useState<Endereco[]>();
    const [colunas, setColunas] = useState<IColuna[]>();
    const [tamanhoFonte, setTamanhoFonte] = useState<number>(0);
    const [permiteEngrenagemGrid, setPermiteEngrenagemGrid] = useState<boolean>(false);
    const [parametros, setParametros] = useState<Parametro[]>([]);
    const [obras, setObras] = useState<Obra[]>([]);

    const valorParametro = useCallback((nome: string) => parametros.find(p => p.Nome == nome)?.Valor, [parametros]);

    useEffect(() => {
        globalMU.blockUI();
        const fetchData = async () => {
            await atualizarParametros();
            await onObterDadosGridEndereco();
        };
        fetchData();
    }, []);

    useEffect(() => {
        globalMU.blockUI();
        const fetchData = async () => {
            await buscaObras();
            await onObterDadosEnderecos();
        };
        fetchData();
    }, [orcamento.Enderecos, orcamento.CodigoCliente]);

    const atualizarParametros = useCallback(async () => {
        setParametros(
            await obterParametros({
                nomes: [
                    "UTILIZAR_ENDERECO_FATURAMENTO_ORC",
                ]
            })
        );
    }, []);

    const onObterDadosEnderecos = useCallback(async () => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Orcamento.DadosEnderecoOrcamento(orcamento.IdentificadorOrcamento);
            setEnderecos(resposta);

            if (resposta == null && orcamento.CodigoCliente <= 7) {
                globalMU.showWarnMessage("Selecione o cliente para visualizar os endereços.");
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [orcamento.IdentificadorOrcamento, orcamento.CodigoCliente]);

    const salvarEnderecos = useCallback(async (enderecos: Endereco[]) => {
        try {
            globalMU.blockUI();
            const resposta = await SalvarEnderecoOrcamento(enderecos);
            setEnderecos(resposta.Enderecos);
            globalMU.showSuccessMessage("Endereço(s) atualizado(s) com sucesso.");
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const onObterDadosGridEndereco = useCallback(async () => {
        try {
            globalMU.blockUI();
            setPermiteEngrenagemGrid(await obterPermissao({ funcionalidade: "ORCAMENTO_ACESSO_ENGRENAGEM_GRID", acao: null }));
            const resposta = await microuniversoApi.Seguranca.obterDadosGrid("orçamento", nomeGrid);
            if (resposta != null) {
                setColunas(resposta.Colunas);
                setTamanhoFonte(resposta.TamanhoFonte);
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const alterarFonte = useCallback(async (tamanho: number) => {
        globalMU.blockUI();
        try {
            await microuniversoApi.Usuario.alterarFonte(nomeGrid, tamanho);
            setTamanhoFonte(tamanho);
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const toggleColunaSelecionada = useCallback(async (nomeColuna: string, selecionada: boolean) => {
        let coluna = { ...colunas.find((c) => c.Nome == nomeColuna) };
        coluna.Selecionada = selecionada;

        const colunas_ = substituirItemEmArray(colunas, (coluna) => coluna.Nome == nomeColuna, coluna);
        setColunas(colunas_);

        await microuniversoApi.Seguranca.atualizarColunasGrid(colunas_, nomeGrid);
    }, [colunas]);

    const buscaObras = useCallback(async () => {
        try {
            const dados = (orcamento?.CodigoCliente ?? 0) > 7 ? await microuniversoApi.Obra.obterObras(orcamento?.CodigoCliente) : [];
            setObras(dados);
        } catch (e) {
            tratamentoErroPadrao(e);
        }
    }, [orcamento.CodigoCliente]);

    const onChangeObra = useCallback(async (obra: Obra) => {
        try {
            await alterarObra({ obra });
        } catch (e) {
            tratamentoErroPadrao(e);
        }
    }, []);

    const toggleSelecionarTodasColunas = useCallback(async (selecionar: boolean) => {
        const colunas_ = colunas.map(c => ({ ...c, Selecionada: selecionar } as IColuna));
        setColunas(colunas_);

        await microuniversoApi.Seguranca.atualizarGrid({
            CodigoUsuario: globalMU.userIdForGrid(),
            Modulo: "Orcamento",
            Token: globalMU.token(),
            NomeGrid: nomeGrid,
            Colunas: colunas_
        });
    }, [colunas]);

    const onAlterarEnderecoEntrega = useCallback((oidEndereco: number, selecionado: boolean) => {
        let enderecos_ = enderecos.map(e => ({ ...e, Entrega: false }));
        if (selecionado) {
            let enderecoSelecionado = { ...enderecos_.find((e) => e.Codigo == oidEndereco) };
            if (enderecoSelecionado != null) {
                enderecoSelecionado.Entrega = true;
                enderecos_ = substituirItemEmArray(enderecos_, (e) => e.Codigo == oidEndereco, enderecoSelecionado);
            }
        }
        setEnderecos(enderecos_);
        salvarEnderecos(enderecos_);
        // 491123 — Entrega/Cobrança/Faturamento não têm representação no slice `orcamento`; capturados
        // à parte para o snapshot de recuperação (ver orcamentoEmAndamentoSlice.ts).
        store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ OidEnderecoEntrega: enderecos_.find(e => e.Entrega)?.Codigo }))
    }, [enderecos, salvarEnderecos]);

    const onAlterarEnderecoCobranca = useCallback((oidEndereco: number, selecionado: boolean) => {
        let enderecos_ = enderecos.map(e => ({ ...e, Cobranca: false }));
        if (selecionado) {
            let enderecoSelecionado = { ...enderecos_.find((e) => e.Codigo == oidEndereco) };
            if (enderecoSelecionado != null) {
                enderecoSelecionado.Cobranca = true;
                enderecos_ = substituirItemEmArray(enderecos_, (e) => e.Codigo == oidEndereco, enderecoSelecionado);
            }
        }
        setEnderecos(enderecos_);
        salvarEnderecos(enderecos_);
        store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ OidEnderecoCobranca: enderecos_.find(e => e.Cobranca)?.Codigo }))
    }, [enderecos, salvarEnderecos]);

    const onAlterarEnderecoFaturamento = useCallback((oidEndereco: number, selecionado: boolean) => {
        let enderecos_ = enderecos.map(e => ({ ...e, Faturamento: false }));
        if (selecionado) {
            let enderecoSelecionado = { ...enderecos_.find((e) => e.Codigo == oidEndereco) };
            if (enderecoSelecionado != null) {
                enderecoSelecionado.Faturamento = true;
                enderecos_ = substituirItemEmArray(enderecos_, (e) => e.Codigo == oidEndereco, enderecoSelecionado);
            }
        }
        setEnderecos(enderecos_);
        salvarEnderecos(enderecos_);
        store.dispatch(atualizarCamposPendentesOrcamentoEmAndamentoAction({ OidEnderecoFaturamento: enderecos_.find(e => e.Faturamento)?.Codigo }))
    }, [enderecos, salvarEnderecos]);

    const permitirVisualizarFaturamento = useMemo(() => valorParametro("UTILIZAR_ENDERECO_FATURAMENTO_ORC") == "S", [valorParametro]);

    return (
        <>
            {enderecos != undefined && obras != undefined && (enderecos == null && obras == null)
                ? <><span>Carregando... Aguarde!</span></>
                : <>
                    <Breadcrumb2Container
                        passos={["Endereços"]}
                        id="GridEnderecosOrcamentoContainer_"
                        exibirBotaoLimpar={false}
                        exibirBotaoVoltar={false}
                    />

                    <GridEnderecosOrcamento
                        somenteLeitura={orcamento.SomenteLeitura}
                        onAlterarEnderecoEntrega={onAlterarEnderecoEntrega}
                        onAlterarEnderecoCobranca={onAlterarEnderecoCobranca}
                        onAlterarEnderecoFaturamento={onAlterarEnderecoFaturamento}
                        handleChange={onChangeObra}
                        obras={obras}
                        obraSelecionada={orcamento.IdObra}
                        orcamentoTransferencia={orcamento.TipoOrcamento == 2}
                        permiteEngrenagemGrid={permiteEngrenagemGrid}
                        permitirVisualizarFaturamento={permitirVisualizarFaturamento}
                        enderecos={enderecos}
                        onToggleColunaSelecionada={toggleColunaSelecionada}
                        onToggleSelecionarTodasColunas={toggleSelecionarTodasColunas}
                        onAlterarFonte={alterarFonte}
                        tamanhoFonte={tamanhoFonte}
                        colunas={colunas || []}
                    />
                </>
            }
        </>
    );
});